import { Component, effect, inject, input } from '@angular/core';

import { EChartsOption, SeriesOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DateRange, Peer, StateService, SubjectType } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { GlobalConstants } from '../../../shared/global-constants';
import { DateService } from '../../services/date.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';


@RegisterComponent('LineChart')
@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
  providers: [provideEcharts()]
})
export class LineChartComponent extends VisualBaseComponent {
  elementId = input.required<string>();
  chartElementId = input<string>();
  numElements = input<number>(3);
  subjectType = input.required<SubjectType>();
  fixedDate = input<boolean>(false);
  rateFromSelector = input<boolean>(false);

  private dateService = inject(DateService);

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;
  noData = false;


  override loadingColor = '#ebebeb';
  override initOptions = { height: 200 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 175,
      top: 20,
      bottom: 10,
      containLabel: true
    },
    title: {
      show: false
    },
    tooltip: {
      show: true,
      trigger: 'axis'
    },
    legend: {
      orient: 'vertical',
      left: 'right',
      top: 25,
      selectedMode: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    yAxis: [
      {
        type: 'value',
        position: 'left',
        show: true,
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            color: '#ccc'
          }
        },
        axisLabel: {
          color: '#243b5b'
        }
      }
    ],
    axisLabel: {
      color: '#002f8e'
    }
  }

  private initalLoad = true;

  constructor() {
    super();

    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const dateRange = this.dateRange();
      const elementId = this.elementId();
      const group = this.filteredGroup();

      if (this.chartInit() && subjectType && (peer || subjectType == SubjectType.institution) && dateRange && elementId && group) {
        this.updateVisual(peer, dateRange, elementId);
      }


    });
  }

  public updateVisual(peer: Peer | undefined, dateRange: DateRange, elementId: string) {
    this.metricService.getElementData(this.institution().iid, elementId, peer).subscribe(metrics => {
      const dateSeries = this.dateService.getDateSeries(this.institution().institutionType, dateRange);

      let lineIndex = 0;
      const lineSeries: SeriesOption[] = [];
      const lineLegendData: string[] = [];
      let hasMetricData = false;

      metrics.forEach(metric => {
        let indexes: number[] = [];
        if (this.subjectType() == SubjectType.peer) {
          indexes = this.visualService.getPeerIndexes(metric.data[0]);
        } else {
          indexes.push(metric.data[0].findIndex((c: number) => {
            return c == this.institution().iid;
          }));
        }

        const institutionIndex = metric.data[0].indexOf(this.institution().iid);

        const lineData: (number | null)[] = [];
        metric.data.forEach(data => {
          const dataRow = dateSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data[0]).getTime() });

          let sum: number | null = null; let count = 0;
          indexes.forEach(i => {
            if (dataRow > -1 && data[i] != null) {
              sum += data[i]; count++;
            }
          });
          let value: number | null = (sum != null && count > 0) ? sum / count : null;

          if (metric.dataType == 'rate') {
            value = data[institutionIndex != -1 ? institutionIndex : 1];
          }

          if (dataRow > -1 && value != null) {
            lineData[dataRow] = VisualService.getValue(value, metric.displayType);
            hasMetricData = true;
          }
        });

        lineLegendData.push(metric.rowTitle);
        lineSeries.push(
          {
            type: 'line',
            lineStyle: {
              type: metric.dataType == 'metric' ? 'solid' : 'dashed'
            },
            name: metric.rowTitle,
            symbol: 'line',
            symbolSize: 0,
            yAxisIndex: 0,
            data: lineData,
            color: this.chartColors().find(c => c.metricId == metric.metricId)?.color ?? GlobalConstants.lineColors[lineIndex++]
          }
        );
      });

      const updateOptions: EChartsOption = {
        grid: {
          top: 20,
          bottom: 10,
          right: 175
        },
        tooltip: {
          formatter: function (params: any) {
            let tooltip: string = '<div style="margin: 0px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><div style="font-size:14px;color:#666;font-weight:400;line-height:1;">' + params[0].name + '</div><div style="margin: 10px 0 0;line-height:1;">';
            params.forEach((param: { color: string; seriesName: string; value: number; }) => {
              if (param.value != undefined) {
                tooltip += '<div style="margin: 10px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color: ' + param.color + ';"></span><span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + param.seriesName + '</span><span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + VisualService.getValueDisplay(param.value, metrics[0].displayType) + '</span><div style="clear:both"></div></div><div style="clear:both"></div></div>'
              }
            });
            tooltip += '</div></div></div>'
            return tooltip;
          }
        },
        xAxis: {
          data: dateSeries.labels
        },
        yAxis: {
          axisLabel: {
            formatter: function (value: number) {
              let displayType = metrics[0].displayType;
              if (displayType == 'PCT') {
                if (!Number.isInteger(value)) {
                  displayType = 'PCT1';
                } else {
                  displayType = 'PCT0'
                }
              }
              return VisualService.getValueDisplay(value, displayType);
            }
          }
        },
        series: lineSeries
      };

      if (!hasMetricData) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      } else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }
        if (this.chartElementId()) {
          this.metricService.getChartElements(this.chartElementId()!, this.dateRange()!.endDate, '', this.numElements()).subscribe(chartElements => {
            this.updateOptions = updateOptions;
            setTimeout(() => {
              if (!this.echartsInstance?.isDisposed() && this.initalLoad) {
                this.echartsInstance.dispatchAction({
                  type: 'legendInverseSelect'
                });
                chartElements.forEach(element => {
                  if (element.plot == true) {
                    this.echartsInstance.dispatchAction({
                      type: 'legendSelect',
                      name: element.title
                    });
                  }
                });
                this.initalLoad = false;
              }
            }, 0);
          });
        } else {
          this.updateOptions = updateOptions;
        }
      }

      if (!this.echartsInstance?.isDisposed()) {
        this.echartsInstance?.hideLoading();
      }
    });
  }
}
